/**
 * The width a grid cell's CONTENT needs, as opposed to the width of the cell's box.
 *
 * Column measurement floors each column at what its rendered cells need. Reading
 * `cell.scrollWidth` for that is circular: a cell in a flexible (fr / auto) column that
 * has stretched to fill spare space reports the stretched width, the floor adopts it,
 * the floor is growth-only and cached, and the column can never shrink again. On a wide
 * window the Invoices "Clients" column absorbed the slack once and was then pinned at
 * that width at every narrower size, pushing the grid off screen.
 *
 * It collects the boxes that content really paints (text runs, and replaced or leaf
 * elements: icons, images, form controls, buttons), groups them into rows by vertical
 * overlap, and returns the WIDEST ROW plus the cell's horizontal padding.
 *
 * Both halves of that matter. Summing within a row is what keeps a header cell honest:
 * its title sits at the left and its sort icon is pushed right with `margin-left: auto`,
 * so the span between them is the track width all over again (measured: the Clients
 * column went from 166px to 465px that way). Taking the maximum across rows is what
 * keeps stacked content honest: the Invoices list renders one block per client
 * (`Invoices.tsx` renderClients), and a five-client cell needs the widest name, not the
 * five added together (measured: 528px for content needing 133px).
 *
 * Wrapped text therefore reports its widest line rather than its unwrapped width. That
 * is deliberate: the offscreen canvas measurement (`measureColumnWidths.ts`) measures
 * each cell's text unwrapped and remains the primary width. This is only its correction,
 * so the floor should never be the larger of the two.
 *
 * Returns 0 for a cell with no painted content.
 */
const LEAF_TAGS = new Set(['SVG', 'IMG', 'INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'CANVAS', 'VIDEO']);

/** One painted piece of content: how wide it is, and the band it occupies vertically. */
type ContentBox = {width: number, top: number, bottom: number};

/**
 * OWA-1291: text wraps only between words, so a word wider than its box is clipped
 * (`overflow: hidden; text-overflow: ellipsis`) instead of split. Its text run still
 * reports the unclipped width (Chromium even returns the overflow as an extra rect on
 * the same line, which the row sum below would add). When the run sits inside an
 * ancestor (within the cell) that clips and has an explicit `max-width`, only the part
 * inside that ancestor's box is painted, so measure just that part. An ancestor without
 * a `max-width` is sized by the column itself, and clipping to it would be the circular
 * measurement described above.
 */
function clipBounds(node: Node, cell: Element): {left: number, right: number} {
    let left = -Infinity;
    let right = Infinity;
    for (let el = node.parentElement; el && el !== cell; el = el.parentElement) {
        const cs = getComputedStyle(el);
        if (cs.overflowX !== 'visible' && cs.maxWidth !== 'none') {
            const r = el.getBoundingClientRect();
            left = Math.max(left, r.left);
            right = Math.min(right, r.right);
        }
    }
    return {left, right};
}

function collectBoxes(node: Node, boxes: ContentBox[], cell: Element): void {
    if (node.nodeType === Node.TEXT_NODE) {
        if (!(node.textContent || '').trim()) return;
        const range = document.createRange();
        range.selectNodeContents(node);
        const clip = clipBounds(node, cell);
        // One rect per line box, so wrapped text arrives as separate stacked rows. A
        // clipped run can return overlapping rects for one line (Chromium adds one for
        // the text before the ellipsis), so merge a node's rects per line into one span.
        const lines: {left: number, right: number, top: number, bottom: number}[] = [];
        for (const r of range.getClientRects()) {
            const left = Math.max(r.left, clip.left);
            const right = Math.min(r.right, clip.right);
            if (right <= left) continue;
            const line = lines.find(l => r.top < l.bottom && r.bottom > l.top);
            if (line) {
                line.left = Math.min(line.left, left);
                line.right = Math.max(line.right, right);
            }
            else lines.push({left, right, top: r.top, bottom: r.bottom});
        }
        for (const l of lines) boxes.push({width: l.right - l.left, top: l.top, bottom: l.bottom});
        return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (LEAF_TAGS.has(el.tagName.toUpperCase())) {
        const r = el.getBoundingClientRect();
        if (r.width > 0) boxes.push({width: r.width, top: r.top, bottom: r.bottom});
        return;
    }
    for (const child of el.childNodes) collectBoxes(child, boxes, cell);
}

export function contentExtent(cell: HTMLElement): number {
    const boxes: ContentBox[] = [];
    for (const child of cell.childNodes) collectBoxes(child, boxes, cell);
    if (boxes.length === 0) return 0;

    // Group by vertical overlap: boxes that share a band are on one line and their
    // widths add; a box that starts below the band in progress opens a new row.
    boxes.sort((a, b) => a.top - b.top);
    let widest = 0;
    let rowWidth = 0;
    let rowBottom = -Infinity;
    for (const box of boxes) {
        if (box.top < rowBottom) {
            rowWidth += box.width;
            if (box.bottom > rowBottom) rowBottom = box.bottom;
        }
        else {
            if (rowWidth > widest) widest = rowWidth;
            rowWidth = box.width;
            rowBottom = box.bottom;
        }
    }
    if (rowWidth > widest) widest = rowWidth;

    const cs = getComputedStyle(cell);
    const paddingX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    return Math.ceil(widest + paddingX);
}
